interface UseCDNImageProps {
  imageSrc: string;

  imageWidth?: number;
  imageHeight?: number;
  format?: string;
  aspectRatio?: string;
  fit?: string;

  mobileWidth?: number;
  mobileHeight?: number;
}

/**
 * @descriptionb - This custom hook is used to replace the image url with the CDN url
 * @param {string} imageSrc - The url of the image
 * @param {number} imageWidth - The width of the image
 * @param {number} imageHeight - The height of the image
 * @param {string} format - The format of the image, that is ".avif" by default
 * @param {number} aspectRatio - The aspect ratio of the image
 * @param {string} fit - The fit of the image
 * @param {number} mobileWidth - The width of the image on mobile
 * @param {number} mobileHeight - The height of the image on mobile
 * @returns {string} CDNImageUrl - The CDN url of the image
 */

const useCDNImage = ({ imageSrc, imageWidth, imageHeight, format, aspectRatio, fit, mobileWidth, mobileHeight }: UseCDNImageProps) => {
  const replaceImageUrlWithCDN = (url: string, width?: number, height?: number) => {
    if (!url) return;
    if (!process.env['IMGIX_URL']) return url;
    if (!process.env['IMG_BLOB_URL']) return url;

    if (url.includes(process.env['IMG_BLOB_URL'])) {
      url = url.replace(process.env['IMG_BLOB_URL'], process.env['IMGIX_URL']); // Assign back to url
    }

    // if the url is absolute url then replace it with the CDN url
    if (url.startsWith('http')) {
      const urlObj = new URL(url);
      urlObj.hostname = process.env['IMGIX_URL'];
      url = urlObj.toString();
    } else {
      url = process.env['IMGIX_URL'] + url;
    }

    url = `${url}?`;

    // Add the width to the url
    if (width) {
      url += `w=${width}&`;
    }

    // Add the height to the url
    if (height) {
      url += `h=${height}&`;
    }

    // Add the format to the url
    if (format) {
      url += `fm=${format}&`;
    }

    // Calculate aspect ratio if both width and height are provided
    if (aspectRatio) {
      url += `ar=${aspectRatio}&`;
    }

    if (fit) {
      url += `fit=${fit}&`;
    }

    // Remove trailing '&' if present
    if (url.endsWith('&')) {
      url = url.slice(0, -1);
    }

    return url;
  };

  // cdn url on desktop
  const CDNImageUrl = replaceImageUrlWithCDN(imageSrc, imageWidth, imageHeight);

  // cdn url on mobile
  const mobileCDNImageUrl = replaceImageUrlWithCDN(imageSrc, mobileWidth, mobileHeight);

  return { CDNImageUrl, mobileCDNImageUrl };
};

export default useCDNImage;
